import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Video, VideoOff, Mic, MicOff, MonitorUp, PhoneOff, X, Link2, Check, Maximize2, Settings, Circle, Shield, Users, Eye, Volume2, VolumeX, Radio, Wifi, Copy, MessageSquare, Send } from 'lucide-react';
interface VideoCallModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'video' | 'audio';
  participantName: string;
  participantInitials: string;
  onEnd?: () => void;
  /** Admin mode — shows recording, monitoring, participant controls */
  isAdmin?: boolean;
  /** Extra participant info for admin (e.g. user + astrologer names) */
  participants?: {
    name: string;
    role: string;
    initials: string;
  }[];
}
export function VideoCallModal({
  isOpen,
  onClose,
  mode,
  participantName,
  participantInitials,
  onEnd,
  isAdmin = false,
  participants
}: VideoCallModalProps) {
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(mode === 'audio');
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [callState, setCallState] = useState<'connecting' | 'connected' | 'ended'>('connecting');
  const [duration, setDuration] = useState(0);
  const [copiedLink, setCopiedLink] = useState<'zoom' | 'meet' | null>(null);
  // Admin-specific state
  const [isRecording, setIsRecording] = useState(false);
  const [isListenOnly, setIsListenOnly] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [copiedMeetingId, setCopiedMeetingId] = useState(false);
  // In-call chat state
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState<{
    text: string;
    isOwn: boolean;
    time: string;
  }[]>([{
    text: 'Здравствуйте! Подключаюсь к звонку.',
    isOwn: false,
    time: '14:00'
  }]);
  const [chatInput, setChatInput] = useState('');
  const chatEndRef = useRef<HTMLDivElement>(null);
  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setCallState('connecting');
      setDuration(0);
      setIsRecording(false);
      setIsListenOnly(false);
      setShowParticipants(false);
      setIsMuted(false);
      setIsVideoOff(mode === 'audio');
      setIsScreenSharing(false);
      return;
    }
    const connectTimer = setTimeout(() => {
      setCallState('connected');
    }, 2000);
    return () => clearTimeout(connectTimer);
  }, [isOpen, mode]);
  // Timer
  useEffect(() => {
    if (!isOpen || callState !== 'connected') return;
    const interval = setInterval(() => {
      setDuration(prev => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [isOpen, callState]);
  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };
  const handleEndCall = () => {
    setCallState('ended');
    setTimeout(() => {
      if (onEnd) onEnd();
      onClose();
    }, 1500);
  };
  const copyLink = (type: 'zoom' | 'meet') => {
    setCopiedLink(type);
    setTimeout(() => setCopiedLink(null), 2000);
  };
  const copyMeetingId = () => {
    setCopiedMeetingId(true);
    setTimeout(() => setCopiedMeetingId(false), 2000);
  };
  const mockMeetingId = 'AST-' + Math.random().toString(36).substring(2, 8).toUpperCase();
  // Reset chat when modal closes
  useEffect(() => {
    if (!isOpen) {
      setShowChat(false);
      setChatInput('');
    }
  }, [isOpen]);
  // Scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [chatMessages, showChat]);
  const handleSendChat = () => {
    if (!chatInput.trim()) return;
    setChatMessages(prev => [...prev, {
      text: chatInput,
      isOwn: true,
      time: new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      })
    }]);
    setChatInput('');
  };
  if (!isOpen) return null;
  const defaultParticipants = participants || [{
    name: participantName,
    role: 'Участник',
    initials: participantInitials
  }];
  return <AnimatePresence data-id="element-18866">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-[100] bg-slate-900/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6" data-id="element-18867">
          <motion.div initial={{
        scale: 0.95,
        opacity: 0,
        y: 20
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} exit={{
        scale: 0.95,
        opacity: 0,
        y: 20
      }} className="w-full max-w-5xl h-[85vh] max-h-[800px] flex relative rounded-3xl overflow-hidden bg-slate-900 border border-slate-800 shadow-2xl" data-id="element-18868">
            {/* Main call area */}
            <div className={`flex-1 flex flex-col relative transition-all duration-300 ${showChat ? 'mr-0' : ''}`} data-id="element-18869">
              {/* Header */}
              <div className="absolute top-0 left-0 right-0 p-4 sm:p-6 flex justify-between items-center z-20 bg-gradient-to-b from-slate-900/90 to-transparent" data-id="element-18870">
                <div className="flex items-center gap-3 flex-wrap" data-id="element-18871">
                  {/* Admin badge */}
                  {isAdmin && <div className="flex items-center gap-1.5 bg-amber-500/20 backdrop-blur-md px-3 py-1.5 rounded-xl border border-amber-500/30" data-id="element-18872">
                      <Shield className="w-3.5 h-3.5 text-amber-400" data-id="element-18873" />
                      <span className="text-amber-300 text-xs font-semibold" data-id="element-18874">
                        Админ
                      </span>
                    </div>}
                  {/* Participant info */}
                  <div className="flex items-center gap-3 bg-slate-800/50 backdrop-blur-md px-4 py-2 rounded-2xl border border-slate-700/50" data-id="element-18875">
                    <div className={`w-2 h-2 rounded-full ${callState === 'connected' ? 'bg-emerald-500' : callState === 'connecting' ? 'bg-amber-500' : 'bg-slate-500'} animate-pulse`} data-id="element-18876" />
                    <span className="text-white font-medium text-sm" data-id="element-18877">
                      {participantName}
                    </span>
                    <div className="w-px h-4 bg-slate-700" data-id="element-18878" />
                    <span className="text-slate-300 font-mono text-sm" data-id="element-18879">
                      {callState === 'connecting' ? 'Соединение...' : callState === 'ended' ? 'Завершен' : formatDuration(duration)}
                    </span>
                  </div>
                  {/* Recording indicator */}
                  {isAdmin && isRecording && callState === 'connected' && <div className="flex items-center gap-1.5 bg-rose-500/20 backdrop-blur-md px-3 py-1.5 rounded-xl border border-rose-500/30 animate-pulse" data-id="element-18880">
                      <Circle className="w-3 h-3 text-rose-500 fill-rose-500" data-id="element-18881" />
                      <span className="text-rose-300 text-xs font-semibold" data-id="element-18882">
                        REC
                      </span>
                    </div>}
                  {/* Listen-only indicator */}
                  {isAdmin && isListenOnly && <div className="flex items-center gap-1.5 bg-blue-500/20 backdrop-blur-md px-3 py-1.5 rounded-xl border border-blue-500/30" data-id="element-18883">
                      <Eye className="w-3.5 h-3.5 text-blue-400" data-id="element-18884" />
                      <span className="text-blue-300 text-xs font-semibold" data-id="element-18885">
                        Прослушка
                      </span>
                    </div>}
                  {/* Connection quality */}
                  {callState === 'connected' && <div className="hidden sm:flex items-center gap-1.5 bg-slate-800/50 backdrop-blur-md px-3 py-1.5 rounded-xl border border-slate-700/50" data-id="element-18886">
                      <Wifi className="w-3.5 h-3.5 text-emerald-400" data-id="element-18887" />
                      <span className="text-emerald-300 text-xs" data-id="element-18888">Отлично</span>
                    </div>}
                </div>
                <div className="flex items-center gap-2" data-id="element-18889">
                  {/* Chat toggle button */}
                  <button onClick={() => setShowChat(!showChat)} className={`p-2.5 rounded-xl transition-colors backdrop-blur-md border ${showChat ? 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30' : 'bg-slate-800/50 text-slate-300 border-slate-700/50 hover:bg-slate-700/50'}`} title="Чат" data-id="element-18890">
                    <MessageSquare className="w-5 h-5" data-id="element-18891" />
                  </button>
                  {isAdmin && <button onClick={() => setShowParticipants(!showParticipants)} className={`p-2.5 rounded-xl transition-colors backdrop-blur-md border ${showParticipants ? 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30' : 'bg-slate-800/50 text-slate-300 border-slate-700/50 hover:bg-slate-700/50'}`} title="Участники" data-id="element-18892">
                      <Users className="w-5 h-5" data-id="element-18893" />
                    </button>}
                  <button onClick={() => {}} className="p-2.5 rounded-xl bg-slate-800/50 hover:bg-slate-700/50 text-slate-300 transition-colors backdrop-blur-md border border-slate-700/50" title="Настройки" data-id="element-18894">
                    <Settings className="w-5 h-5" data-id="element-18895" />
                  </button>
                  <button onClick={() => {
                const el = document.querySelector('[class*="max-w-5xl"]');
                if (el && document.fullscreenElement) {
                  document.exitFullscreen();
                } else if (el) {
                  el.requestFullscreen?.();
                }
              }} className="p-2.5 rounded-xl bg-slate-800/50 hover:bg-slate-700/50 text-slate-300 transition-colors backdrop-blur-md border border-slate-700/50" title="Полный экран" data-id="element-18896">
                    <Maximize2 className="w-5 h-5" data-id="element-18897" />
                  </button>
                  <button onClick={onClose} className="p-2.5 rounded-xl bg-slate-800/50 hover:bg-rose-500/20 text-slate-300 hover:text-rose-400 transition-colors backdrop-blur-md border border-slate-700/50" data-id="element-18898">
                    <X className="w-5 h-5" data-id="element-18899" />
                  </button>
                </div>
              </div>

              {/* Main Video Area */}
              <div className="flex-1 relative bg-slate-950 flex items-center justify-center overflow-hidden" data-id="element-18900">
                {callState === 'ended' ? <div className="text-center" data-id="element-18901">
                    <div className="w-20 h-20 rounded-full bg-slate-800 flex items-center justify-center mx-auto mb-4" data-id="element-18902">
                      <PhoneOff className="w-8 h-8 text-slate-400" data-id="element-18903" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2" data-id="element-18904">
                      Звонок завершен
                    </h3>
                    <p className="text-slate-400" data-id="element-18905">
                      Длительность: {formatDuration(duration)}
                    </p>
                    {isAdmin && isRecording && <p className="text-emerald-400 text-sm mt-2" data-id="element-18906">
                        ✓ Запись сохранена
                      </p>}
                  </div> : <>
                    {/* Participant Video (Large) */}
                    <div className="absolute inset-0 flex items-center justify-center" data-id="element-18907">
                      <div className="w-full h-full bg-gradient-to-br from-slate-800 to-slate-900 flex flex-col items-center justify-center" data-id="element-18908">
                        {callState === 'connecting' ? <div className="flex flex-col items-center" data-id="element-18909">
                            <div className="w-32 h-32 rounded-full bg-slate-800 flex items-center justify-center text-4xl font-bold text-slate-500 mb-6 relative" data-id="element-18910">
                              {participantInitials}
                              <div className="absolute inset-0 rounded-full border-2 border-indigo-500 border-t-transparent animate-spin" data-id="element-18911" />
                            </div>
                            <p className="text-slate-400 text-sm mb-4" data-id="element-18912">
                              {isAdmin ? 'Подключение к сессии...' : 'Вызываем...'}
                            </p>
                            <div className="flex gap-1.5" data-id="element-18913">
                              {[0, 150, 300].map(delay => <div key={delay} className="w-2 h-2 rounded-full bg-indigo-500 animate-bounce" style={{
                        animationDelay: `${delay}ms`
                      }} data-id="element-18914" />)}
                            </div>
                          </div> : <div className="w-40 h-40 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-5xl font-bold text-white shadow-2xl" data-id="element-18915">
                            {participantInitials}
                          </div>}
                      </div>
                    </div>

                    {/* Self Video (Small PiP) */}
                    {!isListenOnly && <motion.div initial={{
                opacity: 0,
                scale: 0.8,
                x: 20,
                y: 20
              }} animate={{
                opacity: 1,
                scale: 1,
                x: 0,
                y: 0
              }} transition={{
                delay: 0.5
              }} className="absolute bottom-28 right-6 w-40 sm:w-56 aspect-video bg-slate-800 rounded-2xl border-2 border-slate-700 shadow-2xl overflow-hidden z-20" data-id="element-18916">
                        {isVideoOff ? <div className="w-full h-full flex items-center justify-center bg-slate-800" data-id="element-18917">
                            <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center text-slate-400 text-sm font-medium" data-id="element-18918">
                              Вы
                            </div>
                          </div> : <div className="w-full h-full bg-slate-700 flex items-center justify-center" data-id="element-18919">
                            <Video className="w-6 h-6 text-slate-500 opacity-50" data-id="element-18920" />
                          </div>}
                        {isMuted && <div className="absolute bottom-2 right-2 p-1 bg-rose-500 rounded-lg" data-id="element-18921">
                            <MicOff className="w-3 h-3 text-white" data-id="element-18922" />
                          </div>}
                      </motion.div>}

                    {/* Admin: Participants Panel */}
                    {isAdmin && showParticipants && callState === 'connected' && <motion.div initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} className="absolute top-20 right-6 w-72 bg-slate-800/90 backdrop-blur-xl rounded-2xl border border-slate-700/50 shadow-2xl z-30 overflow-hidden" data-id="element-18923">
                          <div className="p-4 border-b border-slate-700/50" data-id="element-18924">
                            <div className="flex items-center justify-between" data-id="element-18925">
                              <h4 className="text-white font-semibold text-sm" data-id="element-18926">
                                Участники ({defaultParticipants.length + 1})
                              </h4>
                              <button onClick={copyMeetingId} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition-colors" data-id="element-18927">
                                {copiedMeetingId ? <Check className="w-3 h-3 text-emerald-400" data-id="element-18928" /> : <Copy className="w-3 h-3" data-id="element-18929" />}
                                <span className="font-mono" data-id="element-18930">
                                  {mockMeetingId}
                                </span>
                              </button>
                            </div>
                          </div>
                          <div className="p-2 space-y-1" data-id="element-18931">
                            {/* Admin (you) */}
                            <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-amber-500/10" data-id="element-18932">
                              <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center" data-id="element-18933">
                                <Shield className="w-4 h-4 text-amber-400" data-id="element-18934" />
                              </div>
                              <div className="flex-1 min-w-0" data-id="element-18935">
                                <div className="text-sm font-medium text-white flex items-center gap-1.5" data-id="element-18936">
                                  Вы{' '}
                                  <span className="text-[10px] text-amber-400 bg-amber-500/20 px-1.5 py-0.5 rounded" data-id="element-18937">
                                    Админ
                                  </span>
                                </div>
                                <div className="text-[11px] text-slate-400" data-id="element-18938">
                                  {isListenOnly ? 'Режим прослушки' : 'Активный участник'}
                                </div>
                              </div>
                              <div className="flex gap-1" data-id="element-18939">
                                {isMuted ? <MicOff className="w-3.5 h-3.5 text-rose-400" data-id="element-18940" /> : <Mic className="w-3.5 h-3.5 text-emerald-400" data-id="element-18941" />}
                              </div>
                            </div>
                            {/* Other participants */}
                            {defaultParticipants.map((p, i) => <div key={i} className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-700/50 transition-colors" data-id="element-18942">
                                <div className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center text-indigo-300 text-xs font-bold" data-id="element-18943">
                                  {p.initials}
                                </div>
                                <div className="flex-1 min-w-0" data-id="element-18944">
                                  <div className="text-sm font-medium text-white truncate" data-id="element-18945">
                                    {p.name}
                                  </div>
                                  <div className="text-[11px] text-slate-400" data-id="element-18946">
                                    {p.role}
                                  </div>
                                </div>
                                <div className="flex gap-1.5" data-id="element-18947">
                                  <button className="p-1 rounded-lg hover:bg-slate-600/50 text-slate-400 hover:text-rose-400 transition-colors" title="Заглушить" data-id="element-18948">
                                    <VolumeX className="w-3.5 h-3.5" data-id="element-18949" />
                                  </button>
                                </div>
                              </div>)}
                          </div>
                        </motion.div>}
                  </>}
              </div>

              {/* Controls Bar */}
              <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 flex justify-center items-center z-20 bg-gradient-to-t from-slate-900 to-transparent" data-id="element-18950">
                <div className="flex items-center gap-3 sm:gap-4 bg-slate-800/80 backdrop-blur-xl px-4 sm:px-6 py-3 sm:py-4 rounded-3xl border border-slate-700/50 shadow-2xl" data-id="element-18951">
                  {/* Mic toggle */}
                  <button onClick={() => setIsMuted(!isMuted)} className={`w-11 h-11 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all ${isMuted ? 'bg-rose-500/20 text-rose-500 hover:bg-rose-500/30' : 'bg-slate-700 text-white hover:bg-slate-600'}`} title={isMuted ? 'Включить микрофон' : 'Выключить микрофон'} data-id="element-18952">
                    {isMuted ? <MicOff className="w-5 h-5" data-id="element-18953" /> : <Mic className="w-5 h-5" data-id="element-18954" />}
                  </button>

                  {/* Camera toggle */}
                  <button onClick={() => setIsVideoOff(!isVideoOff)} className={`w-11 h-11 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all ${isVideoOff ? 'bg-rose-500/20 text-rose-500 hover:bg-rose-500/30' : 'bg-slate-700 text-white hover:bg-slate-600'}`} title={isVideoOff ? 'Включить камеру' : 'Выключить камеру'} data-id="element-18955">
                    {isVideoOff ? <VideoOff className="w-5 h-5" data-id="element-18956" /> : <Video className="w-5 h-5" data-id="element-18957" />}
                  </button>

                  {/* Screen share */}
                  <button onClick={() => setIsScreenSharing(!isScreenSharing)} className={`w-11 h-11 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all ${isScreenSharing ? 'bg-indigo-500 text-white hover:bg-indigo-600' : 'bg-slate-700 text-white hover:bg-slate-600'}`} title="Демонстрация экрана" data-id="element-18958">
                    <MonitorUp className="w-5 h-5" data-id="element-18959" />
                  </button>

                  {/* Admin-only controls */}
                  {isAdmin && <>
                      <div className="w-px h-8 bg-slate-700" data-id="element-18960" />
                      {/* Record */}
                      <button onClick={() => setIsRecording(!isRecording)} className={`w-11 h-11 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all ${isRecording ? 'bg-rose-600 text-white hover:bg-rose-700 shadow-lg shadow-rose-900/50' : 'bg-slate-700 text-white hover:bg-slate-600'}`} title={isRecording ? 'Остановить запись' : 'Начать запись'} data-id="element-18961">
                        <Circle className={`w-5 h-5 ${isRecording ? 'fill-white' : ''}`} data-id="element-18962" />
                      </button>
                      {/* Listen-only mode */}
                      <button onClick={() => {
                  setIsListenOnly(!isListenOnly);
                  if (!isListenOnly) {
                    setIsMuted(true);
                    setIsVideoOff(true);
                  }
                }} className={`w-11 h-11 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all ${isListenOnly ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30' : 'bg-slate-700 text-white hover:bg-slate-600'}`} title={isListenOnly ? 'Выйти из режима прослушки' : 'Режим прослушки (невидимый)'} data-id="element-18963">
                        <Eye className="w-5 h-5" data-id="element-18964" />
                      </button>
                    </>}

                  <div className="w-px h-8 bg-slate-700 mx-1" data-id="element-18965" />

                  {/* End call */}
                  <button onClick={handleEndCall} className="w-14 sm:w-16 h-11 sm:h-12 rounded-full flex items-center justify-center bg-rose-600 hover:bg-rose-700 text-white transition-all shadow-lg shadow-rose-900/50" title="Завершить звонок" data-id="element-18966">
                    <PhoneOff className="w-5 sm:w-6 h-5 sm:h-6" data-id="element-18967" />
                  </button>
                </div>
              </div>

              {/* External Links Overlay */}
              {callState === 'connecting' && <div className="absolute top-20 sm:top-24 left-4 sm:left-6 z-20 flex flex-col gap-3" data-id="element-18968">
                  <div className="bg-slate-800/80 backdrop-blur-md p-4 rounded-2xl border border-slate-700/50 w-64 sm:w-72" data-id="element-18969">
                    <p className="text-xs text-slate-400 mb-3" data-id="element-18970">
                      Предпочитаете другой сервис?
                    </p>
                    <div className="space-y-2" data-id="element-18971">
                      <button onClick={() => copyLink('zoom')} className="w-full flex items-center justify-between px-3 py-2 rounded-xl border border-blue-500/30 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-colors text-sm" data-id="element-18972">
                        <span className="flex items-center gap-2" data-id="element-18973">
                          <Link2 className="w-4 h-4" data-id="element-18974" />
                          Ссылка Zoom
                        </span>
                        {copiedLink === 'zoom' && <Check className="w-4 h-4" data-id="element-18975" />}
                      </button>
                      <button onClick={() => copyLink('meet')} className="w-full flex items-center justify-between px-3 py-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-colors text-sm" data-id="element-18976">
                        <span className="flex items-center gap-2" data-id="element-18977">
                          <Link2 className="w-4 h-4" data-id="element-18978" />
                          Ссылка Google Meet
                        </span>
                        {copiedLink === 'meet' && <Check className="w-4 h-4" data-id="element-18979" />}
                      </button>
                    </div>
                  </div>
                </div>}
            </div>

            {/* In-call Chat Panel */}
            <AnimatePresence data-id="element-18980">
              {showChat && <motion.div initial={{
            width: 0,
            opacity: 0
          }} animate={{
            width: 320,
            opacity: 1
          }} exit={{
            width: 0,
            opacity: 0
          }} transition={{
            duration: 0.3,
            ease: 'easeInOut'
          }} className="h-full flex flex-col bg-slate-800 border-l border-slate-700 overflow-hidden" data-id="element-18981">
                  {/* Chat Header */}
                  <div className="p-4 border-b border-slate-700 flex items-center justify-between flex-shrink-0" data-id="element-18982">
                    <div className="flex items-center gap-2" data-id="element-18983">
                      <MessageSquare className="w-4 h-4 text-indigo-400" data-id="element-18984" />
                      <h4 className="text-white font-semibold text-sm" data-id="element-18985">
                        Чат звонка
                      </h4>
                    </div>
                    <button onClick={() => setShowChat(false)} className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors" data-id="element-18986">
                      <X className="w-4 h-4" data-id="element-18987" />
                    </button>
                  </div>

                  {/* Chat Messages */}
                  <div className="flex-1 overflow-y-auto p-3 space-y-3" data-id="element-18988">
                    {chatMessages.map((msg, i) => <div key={i} className={`flex ${msg.isOwn ? 'justify-end' : 'justify-start'}`} data-id="element-18989">
                        <div className={`max-w-[85%] px-3 py-2 rounded-2xl ${msg.isOwn ? 'bg-indigo-600 text-white rounded-br-sm' : 'bg-slate-700 text-slate-200 rounded-bl-sm'}`} data-id="element-18990">
                          <p className="text-xs" data-id="element-18991">{msg.text}</p>
                          <span className={`text-[9px] mt-1 block ${msg.isOwn ? 'text-indigo-200' : 'text-slate-400'}`} data-id="element-18992">
                            {msg.time}
                          </span>
                        </div>
                      </div>)}
                    <div ref={chatEndRef} data-id="element-18993" />
                  </div>

                  {/* Chat Input */}
                  <div className="p-3 border-t border-slate-700 flex-shrink-0" data-id="element-18994">
                    <div className="flex items-center gap-2" data-id="element-18995">
                      <input type="text" placeholder="Сообщение..." value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => {
                  if (e.key === 'Enter' && chatInput.trim()) {
                    handleSendChat();
                  }
                }} className="flex-1 bg-slate-700 border border-slate-600 rounded-xl px-3 py-2 text-sm text-white placeholder-slate-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" data-id="element-18996" />
                      <button onClick={handleSendChat} disabled={!chatInput.trim()} className="p-2 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-18997">
                        <Send className="w-4 h-4" data-id="element-18998" />
                      </button>
                    </div>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}